/* StripeReturnBanner.jsx
 *
 * Prominent in-app success banner that appears after a Stripe Checkout
 * return. Slides in from the top of the page, auto-dismisses after 12 s,
 * and includes a CTA button to take the user straight to the relevant dialog.
 *
 * Usage (in AppShell):
 *   const [returnBanner, setReturnBanner] = useState(null);
 *   // set via useStripeReturn callbacks:
 *   //   setReturnBanner({ icon, headline, body, ctaLabel, onCta })
 *   <StripeReturnBanner info={returnBanner} onDismiss={() => setReturnBanner(null)} />
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, X, ArrowRight } from 'lucide-react';

export default function StripeReturnBanner({ info, onDismiss }) {
  const [visible, setVisible] = useState(false);

  // Slide in when info is set, slide out on dismiss.
  useEffect(() => {
    if (!info) { setVisible(false); return; }
    // micro-delay so the enter transition fires
    const t = setTimeout(() => setVisible(true), 30);
    // auto-dismiss after 12 s
    const a = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss?.(), 400);
    }, 12000);
    return () => { clearTimeout(t); clearTimeout(a); };
  }, [info, onDismiss]);

  if (!info) return null;

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => onDismiss?.(), 400);
  };

  const handleCta = () => {
    info.onCta?.();
    handleDismiss();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="stripe-return-banner"
      style={{
        position: 'fixed',
        top: visible ? '1rem' : '-6rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        transition: 'top 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
        width: 'min(92vw, 480px)',
        pointerEvents: 'auto',
      }}
    >
      <div className="flex items-start gap-3 rounded-lg border border-emerald-500/40 bg-card px-4 py-3 shadow-2xl">
        {/* Icon */}
        <div className="mt-0.5 flex-shrink-0 rounded-full bg-emerald-500/15 p-1.5">
          <CheckCircle2 className="h-4.5 w-4.5 text-emerald-500" style={{ width: 18, height: 18 }} />
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold leading-snug" data-testid="stripe-banner-headline">
            {info.headline}
          </p>
          {info.body && (
            <p className="mt-0.5 text-[11.5px] text-muted-foreground" data-testid="stripe-banner-body">
              {info.body}
            </p>
          )}
        </div>

        {/* CTA */}
        {info.ctaLabel && (
          <button
            type="button"
            onClick={handleCta}
            data-testid="stripe-banner-cta"
            className="flex-shrink-0 inline-flex items-center gap-1 rounded-sm bg-emerald-500 px-2.5 py-1.5 text-[11.5px] font-semibold text-white hover:bg-emerald-600 transition-colors"
          >
            {info.ctaLabel}
            <ArrowRight className="h-3 w-3" />
          </button>
        )}

        {/* Dismiss */}
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="stripe-banner-dismiss"
          className="flex-shrink-0 rounded p-1 text-muted-foreground hover:bg-secondary"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Progress bar auto-dismiss indicator */}
      {visible && (
        <div
          className="mt-1 h-0.5 rounded-full bg-emerald-500/60"
          style={{
            width: '100%',
            animation: 'stripe-banner-shrink 12s linear forwards',
          }}
        />
      )}

      <style>{`
        @keyframes stripe-banner-shrink {
          from { width: 100%; }
          to   { width: 0%;   }
        }
      `}</style>
    </div>
  );
}
